import { FullStory, init } from '@fullstory/browser'

import { User } from '@/features/auth'
import { Customer } from '@/features/auth/domain/customer'

import { error } from '../logger'

interface IFullStoryAnalyticsService {
  readonly shouldRun: () => boolean
  readonly orgId: string
}

export class FullStoryAnalyticsService {
  private orgId: string
  private readonly shouldRun: () => boolean
  private isInitialized = false
  private isCapturing = false

  constructor(props: IFullStoryAnalyticsService) {
    this.orgId = props.orgId
    this.shouldRun = props.shouldRun
  }

  // Loads FullStory and starts capturing. Must only be called once the user is
  // authenticated: pre-login visitors have not consented to tracking
  startCapturing() {
    if (!this.shouldRun() || this.isCapturing) {
      return
    }

    try {
      if (this.isInitialized) {
        FullStory('restart')
      } else {
        init({
          orgId: this.orgId,
        })
        this.isInitialized = true
      }
      this.isCapturing = true
    } catch (e) {
      if (e instanceof Error) {
        error('Failed to initialize FullStory analytics', { response: e })
      }
    }
  }

  private getFullName(user: User) {
    const nameParts = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
    return nameParts || ''
  }

  init(customer: Customer, user: User, loggedInAsCustomer: boolean, tokenRequestedFrom?: string) {
    try {
      FullStory('setIdentity', {
        uid: user.id,
        properties: {
          name: this.getFullName(user),
          email: user.email,
          emailDomain: user.email.split('@')[1],
          source: 'ship',
          customerId: customer._id,
          customerName: customer.name,
          userId: user.id,
          userLanguage: user.preferredLanguage,
          roles: user.roles ? user.roles.join(',') : '',
          enabledFeatures: customer.enabledFeatures ? customer.enabledFeatures.join(',') : '',
          tokenRequestedFrom,
          loggedInFromTms: loggedInAsCustomer,
          screenDimensions: `${window.screen.width} x ${window.screen.height}`,
        },
      })
    } catch (e) {
      if (e instanceof Error) {
        error('There has been an error during the initiation of fullstory analytics', { response: e })
      }
    }
  }

  reset = () => {
    if (!this.shouldRun() || !this.isCapturing) {
      return
    }

    try {
      FullStory('setIdentity', {
        anonymous: true,
      })
      FullStory('shutdown')
    } catch (e) {
      if (e instanceof Error) {
        error('There has been an error during the shutdown of fullstory analytics', { response: e })
      }
    }
    this.isCapturing = false
  }

  sendEvent(name: string, data: Record<string, unknown> = {}) {
    if (!this.shouldRun() || !this.isCapturing) {
      return
    }

    try {
      FullStory('trackEvent', {
        name: name,
        properties: data,
      })
    } catch (e) {
      if (e instanceof Error) {
        error('There has been an error during sending event to fullstory analytics', { response: e })
      }
    }
  }

  update(updates: Record<string, unknown>) {
    if (!this.shouldRun() || !this.isCapturing) {
      return
    }

    try {
      FullStory('setProperties', {
        type: 'user',
        properties: updates,
      })
    } catch (e) {
      if (e instanceof Error) {
        error('There has been an error during updating fullstory analytics', { response: e })
      }
    }
  }
}
